import { PubSubService } from '@ohif/core';
import { authHttp, HttpException } from '../http';
import { getLoginUrl } from '../config/roots';

export enum AccountRoleEnum {
  USER = 'USER',
  ENVIRONMENT_ADMIN = 'ENVIRONMENT_ADMIN',
  REVIEWER = 'REVIEWER',
}

export enum EnvironmentSizeEnum {
  SMALL = 'SMALL',
  LARGE = 'LARGE',
}

export type Account = {
  id: string;
  email: string;
  username: string;
  role: AccountRoleEnum;
  size: EnvironmentSizeEnum;
};

const EVENTS = {
  SESSION_CHANGED: 'event::sycai:SessionService:sessionChanged',
};

/**
 * verifySession() gates the entire first render: it is awaited inside
 * preRegistration, which is awaited by ExtensionManager.registerExtensions
 * (ExtensionManager.ts:236,277) and then by appInit.js:123, and App.tsx:71-82
 * renders `null` until appInit resolves. HttpClient passes no `timeout` and
 * axios's default is 0 — none. So a host that completes the TCP handshake and
 * then never answers (hung gateway, saturated backend, a firewall that DROPs
 * mid-stream) leaves the user on a permanently blank page with no study list and
 * no console line at all. ECONNREFUSED cannot reveal this: it fails in
 * milliseconds. D-3 names a timeout FIRST among the failures that must fail
 * visibly WITHOUT blocking boot, so it has to actually be able to occur.
 */
const VERIFY_SESSION_TIMEOUT_MS = 8000;

/**
 * `AbortSignal.timeout` is a browser API. Every browser this viewer targets has
 * it (Chrome 103+, Firefox 100+, Safari 16+), but jsdom 20.0.3 — what
 * jest-environment-jsdom 29.7.0 pins, and therefore what `pnpm test` runs — does
 * NOT: `typeof AbortSignal.timeout === 'undefined'` there, while
 * `AbortSignal.abort` exists. Calling it unguarded throws a TypeError from inside
 * verifySession's own try, which would swap the real transport failure that M1's
 * registration.test.ts drives for a bogus one and quietly stop testing the thing.
 *
 * Degrading to "no timeout" under jest is safe and deliberate: there is no
 * network there, and the request cannot hang. Do not replace this with an
 * AbortController + setTimeout pair to avoid the branch — that leaves a live
 * 8-second timer behind on the fast path and jest will complain that a worker
 * failed to exit.
 */
function verifySessionTimeoutSignal(): AbortSignal | undefined {
  return typeof AbortSignal?.timeout === 'function'
    ? AbortSignal.timeout(VERIFY_SESSION_TIMEOUT_MS)
    : undefined;
}

/**
 * A 200 is not an account. `authHttp.get<Account>` is a CAST, not a check, so
 * without this any 2xx body — HTML, `null`, an unrelated JSON object — becomes
 * the session: verifySession RESOLVES, getAccount() hands every later consumer
 * that garbage, and the role gate silently evaluates `undefined === 'REVIEWER'`.
 *
 * This is documented as live in this very repo: sycai-local.js:39-44 records that
 * the dev server's historyApiFallback answers unknown paths with index.html and
 * HTTP 200, and mitigates it by pointing environmentManagement at a DEAD PORT.
 * A configuration choice was standing in for a code guard, and any reverse proxy
 * that answers an expired session with a 200 login page reproduces it in
 * deployment.
 *
 * `role` is the field to key on rather than `id`/`email`/`username`: it is the
 * only one the fork consumes (the role gate), so it is both necessary and
 * sufficient, and validating fields we never read would reject a valid-but-partial
 * account for no benefit. Not narrowed to the AccountRoleEnum members either — a
 * backend that adds a fourth role must not silently lose the session.
 *
 * A string body fails `typeof === 'object'`; `null` fails the truthiness test; an
 * array passes it but has no string `role`.
 */
function isAccount(data: unknown): data is Account {
  return (
    Boolean(data) &&
    typeof data === 'object' &&
    typeof (data as { role?: unknown }).role === 'string' &&
    (data as { role: string }).role.length > 0
  );
}

/**
 * The fork's one auth object (design D7): the account, the role and the
 * Authorization header are one concern, and a second fork service named
 * "auth-something" alongside OHIF's userAuthenticationService invites injecting
 * the wrong one.
 *
 * It earns registration on grounds DomainManagementService did not (D8): it
 * holds state, and upstream provides NO auth event —
 * `UserAuthenticationService.EVENTS = {}` and `_broadcastEvent` is never called
 * there — so consumers would otherwise poll. AGENTS.md's standing guidance is
 * to prefer pub/sub over useEffect.
 *
 * THERE IS NO refresh(). Not a throwing stub, not an empty body — absent
 * (§5.5). There is no refresh endpoint; develop carried the vestige of one as an
 * empty method (CustomAuthenticationService.ts:80) plus an appInit.js handler
 * that logged "trying to refresh session" before calling handleUnauthenticated.
 * An expired session goes to handleUnauthenticated, which is the only path that
 * ever ran.
 */
export class SessionService extends PubSubService {
  public static readonly EVENTS = EVENTS;

  public static readonly REGISTRATION = {
    name: 'sessionService',
    create: () => new SessionService(),
  };

  private account: Account | null = null;

  constructor() {
    super(EVENTS);
  }

  public getAccount(): Account | null {
    return this.account;
  }

  public getRole(): AccountRoleEnum | null {
    return this.account?.role ?? null;
  }

  public isReviewer(): boolean {
    return this.getRole() === AccountRoleEnum.REVIEWER;
  }

  /**
   * Runs inside preRegistration, which is awaited before any provider or route
   * renders (App.tsx:71-82 returns null until appInit resolves). That is parent
   * §4's improvement: it replaces the fork's post-render effect at
   * App.tsx:77-79, which races.
   *
   * Throws on failure. The caller decides what a failure means — see the 401
   * branch in index.tsx, and plan deviation D-3.
   */
  public async verifySession(): Promise<Account> {
    try {
      const { data, status } = await authHttp.get<Account>('accounts/me', {
        signal: verifySessionTimeoutSignal(),
      });

      // An abort produces no `response`, so HttpClient.toException already maps
      // it to HttpException(0) and index.tsx's D-3 branch handles it unchanged.
      if (!isAccount(data)) {
        throw new HttpException(
          status,
          `accounts/me answered ${status} with a body that is not an Account`
        );
      }

      this.setAccount(data);
      return data;
    } catch (error) {
      this.setAccount(null);
      throw error;
    }
  }

  public async logOut(): Promise<void> {
    try {
      await authHttp.post('auth/log-out', {});
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // A failed log-out must not prevent the local teardown or the redirect.
      // `error` is deliberately unused and deliberately NAMED. An OPTIONAL catch
      // binding (`} catch {`) — which is this tree's usual style, e.g.
      // utils/cookies.ts:33 — crashes @babel/plugin-transform-regenerator when it
      // appears inside an ASYNC function: emit.ts:836 reads `.name` off the null
      // param and throws "Cannot read properties of null (reading 'name')",
      // failing the whole test suite at TRANSFORM time. That plugin is in the
      // `test` env only (babel.config.js), so dev and production builds are
      // unaffected — which is exactly why this must not be "tidied" back.
    } finally {
      this.setAccount(null);
      localStorage.removeItem('isAuthenticated');
      window.location.replace(getLoginUrl());
    }
  }

  private setAccount(account: Account | null): void {
    this.account = account;
    // Carries the ROLE, never the account object — §6.1. Subscribers that need
    // the account call getAccount().
    this._broadcastEvent(EVENTS.SESSION_CHANGED, { role: account?.role ?? null });
  }
}
